import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPaperPlane, FaTimes, FaRobot } from "react-icons/fa";
import { chatbot } from "../services/ClientService";

const ChatbotWidget = () => {
    const [query, setQuery] = useState("");
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showChat, setShowChat] = useState(false);
    const chatContainerRef = useRef(null);
    const [isFirstOpen, setIsFirstOpen] = useState(true);

    const hitLimit = messages.filter(m => m.sender === 'user').length >= 4;

    useEffect(() => {
        if (showChat && isFirstOpen) {
            setMessages([{ text: "Hi There! How can I help you with our products today?", sender: "bot" }]);
            setIsFirstOpen(false); // Ensure initial message only appears once per session
        }
    }, [showChat, isFirstOpen]);

    // Effect to scroll to the bottom of the chat when new messages are added
    useEffect(() => {
        if (chatContainerRef.current) {
            // Smooth scroll to the bottom
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messages, loading]); // Trigger scroll on new messages or loading state change

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;

        const userCount = messages.filter(m => m.sender === 'user').length;
        if (userCount >= 4) {
            setError('You’ve hit the 4‑message limit. Please start a new chat.');
            return;
        }

        const updatedMessages = [...messages, { text: query, sender: "user" }];
        setMessages(updatedMessages);
        setQuery("");

        setLoading(true);
        setError(null);

        try {
            const response = await chatbot({ messages: updatedMessages });

            console.log("API Response:", response);

            if (response.data?.answer) {
                setMessages(prev => [...prev, { text: response.data.answer, sender: "bot" }]);
            } else {
                setError(response.error || "Something went wrong.");
                console.error("API Error:", response.error);
                const errorMessage = { text: response.error || "Something went wrong.", sender: "bot", isError: true };
                setMessages((prevMessages) => [...prevMessages, errorMessage]);
            }
        } catch (err) {
            setError(err.message || "Something went wrong.");
            console.error("Fetch Error:", err);
            const errorMessage = { text: err.message || "Something went wrong.", sender: "bot", isError: true };
            setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseChat = () => {
        setShowChat(false);
        setMessages([]);
        setIsFirstOpen(true); // Reset for fresh start
    };

    // Handle opening the chat window
    const handleOpenChat = () => {
        setShowChat(true);
    };

    const handleNewChat = () => {
        setMessages([]);
        setIsFirstOpen(true);
        setError(null);
    };

    return (
        <>
            {/* Toggle Button */}
            <button
                onClick={showChat ? handleCloseChat : handleOpenChat}
                className={`fixed bottom-5 right-5 z-[1000] flex items-center justify-center w-14 h-14 rounded-full text-white shadow-lg transition-all duration-300 ease-out ${showChat
                    ? "bg-gradient-to-br from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600" // Red gradient when open (close button)
                    : "bg-gradient-to-br from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" // Blue gradient when closed (open button)
                    }`}
                aria-label={showChat ? "Close Chat" : "Open Chat"}
            >
                <AnimatePresence initial={false} mode="wait">
                    <motion.div
                        key={showChat ? "times" : "plane"}
                        initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                        transition={{ duration: 0.2 }}
                    >
                        {/* Conditionally render close (Times) or open (PaperPlane) icon */}
                        {showChat ? <FaTimes size={22} /> : <FaRobot size={24} />}
                    </motion.div>
                </AnimatePresence>
            </button>

            {/* Chat Modal */}
            <AnimatePresence>
                {showChat && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }} // Initial animation state
                        animate={{ opacity: 1, y: 0, scale: 1 }} // Animate to visible state
                        exit={{ opacity: 0, y: 30, scale: 0.9 }} // Animate on exit
                        transition={{ type: "spring", stiffness: 150, damping: 20 }} // Spring animation
                        // Styling for the chat window: fixed position, size constraints, background, border, shadow, flex layout
                        className="fixed bottom-[calc(3.5rem+1.25rem)] right-5 w-[calc(100%-2.5rem)] sm:w-96 max-h-[70vh] bg-white border border-gray-200 rounded-xl shadow-xl z-[999] flex flex-col overflow-hidden dark:bg-gray-800 dark:border-gray-700"
                    >
                        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Ask about a product</h3>
                            {/* ← NEW CHAT BUTTON */}
                            <button
                                onClick={handleNewChat}
                                className="text-sm font-semibold text-blue-500 hover:underline mr-4"
                            >
                                New Chat
                            </button>
                            <button onClick={handleCloseChat} className="text-gray-500 hover:text-gray-700">
                                <FaTimes className="h-4 w-4"/>
                            </button>
                        </div>
                        <div ref={chatContainerRef} className="flex-grow overflow-y-auto p-4 space-y-4">
                            {messages.map((msg, index) => (
                                <div
                                    key={index}
                                    className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"
                                        }`}
                                >
                                    <div
                                        // Styling for message bubbles: max width, padding, border radius, conditional background/text colors
                                        className={`max-w-[80%] p-3 rounded-xl ${msg.sender === "user"
                                            ? "bg-blue-500 text-white rounded-br-none" // User message style
                                            : msg.isError // Error message style
                                                ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded-bl-none"
                                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 rounded-bl-none" // Bot message style
                                            }`}
                                    >
                                        {/* Display message text */}
                                        <p className="text-sm break-words">{msg.text}</p>
                                    </div>
                                </div>
                            ))}
                            <div className="p-2 text-sm text-red-600">
                                {hitLimit && 'Message limit reached. Please start a new chat.'}
                            </div>
                            {/* Loading Indicator */}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="max-w-[80%] p-3 rounded-xl rounded-bl-none bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 animate-pulse">
                                        <p className="text-sm">Typing...</p>
                                    </div>
                                </div>
                            )}
                            {error && (
                                <div className="mb-2 p-3 rounded-md bg-red-100 text-red-800 self-end" style={{ maxWidth: "80%" }}>
                                    <p className="text-sm">{error}</p>
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                            <form onSubmit={handleSubmit} className="flex items-center space-x-3">
                                <textarea
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        // Submit on Enter key press (Shift+Enter for new line)
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault(); // Prevent default newline behavior
                                            handleSubmit(e); // Trigger form submission
                                        }
                                    }}
                                    placeholder="Type your question here..."
                                    className="flex-grow border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white dark:placeholder-gray-400 resize-none"
                                    rows={1} // Start with 1 row, expands automatically if needed (CSS might be needed for auto-expand)
                                    style={{ maxHeight: '80px', overflowY: 'auto' }} // Limit height and allow scrolling within textarea
                                    disabled={loading || hitLimit}
                                />
                                <button
                                    type="submit"
                                    className={`p-2 rounded-lg text-white transition-all duration-200 ${loading || !query.trim()
                                        ? "bg-gray-400 cursor-not-allowed" // Disabled style
                                        : "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:shadow-md" // Enabled style
                                        }`}
                                    disabled={loading || !query.trim() || hitLimit} // Disable if loading or query is empty
                                    aria-label="Send Message" // Accessibility label
                                >
                                    <FaPaperPlane size={18} />
                                </button>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ChatbotWidget;